import React, { useMemo } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { CarIcon } from "../../icons/CarIcon";
import CoffeeOutlinedIcon from "@mui/icons-material/CoffeeOutlined";
import { Box, Button, Card, IconButton, LinearProgress, Typography } from "@mui/material";
import { BooleanInput, EditBase, NumberInput, useGetOne, useGetRecordId, useRefresh } from "react-admin";
import { type Path, useFieldArray, useFormContext, useFormState, useWatch } from "react-hook-form";
import { useThemeColor } from "../../hooks/useThemeColor";
import { cardTitleStyle, overviewCardStyle } from "../../misc/backofficeTheme";
import { SelectInput } from "../../inputs/SelectInput";
import { Column } from "../../misc/Column";
import { Form2 } from "../../misc/Form2";
import { FormCard } from "../../misc/FormCard";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Row } from "../../misc/Row";
import { SaveButton2 } from "../../buttons/SaveButton2";
import type { RecommendationSettings } from "../../providers/instructorRecommendationSettingsProvider";
import { type WorkTimeDay, WorkTimeDayEnum, type WorkTimes } from "../../providers/instructorWorkTimesProvider";
import { sortAndMergeTimeRanges, Time, TimeRange } from "../../model/TimeRange";
import { calculateDailyAvailability } from "../../model/calculateDailyAvailability";
import _clamp from "lodash/clamp";
import isEqual from "lodash/isEqual";
import { TimeInput } from "../../inputs/TimeInput";
import { DateTime } from "luxon";

const _workTimeDays = WorkTimeDayEnum.options;

const _workTimeDayLabels: Record<WorkTimeDay, string> = {
  monday: "Montag",
  tuesday: "Dienstag",
  wednesday: "Mittwoch",
  thursday: "Donnerstag",
  friday: "Freitag",
  saturday: "Samstag",
  sunday: "Sonntag",
};

export function InstructorAvailabilitySettingsTab() {
  const instructorId = useGetRecordId() as string;
  const { data: workTimes } = useGetOne<WorkTimes>("instructorWorkTimes", { id: instructorId });
  const { data: recommendationSettings } = useGetOne<RecommendationSettings>("instructorRecommendationSettings", {
    id: instructorId,
  });

  if (!workTimes || !recommendationSettings) {
    return <LoadingIndicator />;
  }

  return (
    <Box sx={{ containerType: "inline-size" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: "20px",
          alignItems: "start",
          "@container (min-width: 1100px)": {
            gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)",
          },
        }}
      >
        <Column gap="20px" sx={{ minWidth: 0 }}>
          <_PlannedAvailabilityCard workTimes={workTimes} recommendationSettings={recommendationSettings} />
          <_WorkTimesCards instructorId={instructorId} recommendationSettings={recommendationSettings} />
        </Column>
        <Box sx={{ minWidth: 0 }}>
          <_RecommendationSettingsCard instructorId={instructorId} />
        </Box>
      </Box>
    </Box>
  );
}

function _PlannedAvailabilityCard(props: { recommendationSettings: RecommendationSettings; workTimes: WorkTimes }) {
  const { recommendationSettings, workTimes } = props;
  const { maxLessonUnitsPerWeek } = recommendationSettings;
  const weeklyPlannedAvailableLessonUnits = useMemo(() => {
    let weeklyPlannedAvailableLessonUnits = 0;
    for (const timeRanges of Object.values(omitId(workTimes))) {
      if (timeRanges.length > 0) {
        const dailyAvailability = calculateDailyAvailability(timeRanges, recommendationSettings);
        weeklyPlannedAvailableLessonUnits += dailyAvailability.numLessonUnits;
      }
    }
    return weeklyPlannedAvailableLessonUnits;
  }, [workTimes, recommendationSettings]);
  const percent = maxLessonUnitsPerWeek > 0 ? (weeklyPlannedAvailableLessonUnits / maxLessonUnitsPerWeek) * 100 : 0;
  const primaryColor = useThemeColor("primary");

  return (
    <Card sx={{ ...overviewCardStyle }}>
      <Typography sx={cardTitleStyle}>Geplante Verfügbarkeit</Typography>
      <LinearProgress
        variant="determinate"
        value={_clamp(percent, 0, 100)}
        sx={{
          height: "8px",
          borderRadius: "999px",
          backgroundColor: primaryColor.ultraLight,
          "& .MuiLinearProgress-bar": { borderRadius: "999px" },
        }}
      />
      <Typography component="div" variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {formatNumber(weeklyPlannedAvailableLessonUnits)} UE pro Woche
        {maxLessonUnitsPerWeek > 0 && ` (${Math.round(percent)}% von max. ${formatNumber(maxLessonUnitsPerWeek)} UE)`}
      </Typography>
    </Card>
  );
}

type TimeRangeFormValue = { start?: Time; end?: Time };
type WorkTimesFormValues = Record<WorkTimeDay, Array<TimeRangeFormValue>>;

function _WorkTimesCards(props: { instructorId: string; recommendationSettings: RecommendationSettings }) {
  const { instructorId, recommendationSettings } = props;
  const refresh = useRefresh();
  return (
    <EditBase<WorkTimes>
      resource="instructorWorkTimes"
      id={instructorId}
      redirect={false}
      transform={(formData: WorkTimes) => {
        const data: Partial<WorkTimes> = {};
        for (const day of _workTimeDays) {
          const timeRanges = formData[day];
          data[day] = sortAndMergeTimeRanges(timeRanges ?? []);
        }
        return data;
      }}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          refresh();
        },
        onError: (error) => handleError("Fehler beim Speichern der Änderungen.", error),
      }}
    >
      <Form2<WorkTimesFormValues> validate={validateWorkTimes as any}>
        <Column gap="20px">
          {_workTimeDays.map((day) => (
            <_WorkTimeDayCard key={day} day={day} recommendationSettings={recommendationSettings} />
          ))}
        </Column>
      </Form2>
    </EditBase>
  );
}

function _WorkTimeDayCard({
  day,
  recommendationSettings,
}: {
  day: WorkTimeDay;
  recommendationSettings: RecommendationSettings;
}) {
  const { control } = useFormContext<WorkTimesFormValues>();
  const { fields, append, remove } = useFieldArray<WorkTimesFormValues>({ control, name: day });
  const timeRanges: Array<TimeRangeFormValue> = useWatch({ control, name: day }) ?? [];
  const availability = calculateDailyAvailability(timeRanges.filter(isValidTimeRange), recommendationSettings);
  const { defaultValues } = useFormState<WorkTimesFormValues>({ control, name: day });
  const isDirty = !isEqual(timeRanges, defaultValues?.[day] ?? []);

  return (
    <Card sx={{ ...overviewCardStyle }}>
      <Row
        sx={{
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "4px 16px",
          "@container (max-width: 600px)": {
            flexDirection: "column",
            alignItems: "flex-start",
            marginBottom: "6px",
            "& > .MuiTypography-root": { marginBottom: 0 },
          },
        }}
      >
        <Typography sx={cardTitleStyle}>{_workTimeDayLabels[day]}</Typography>
        <Row sx={{ alignContent: "baseline", flexShrink: 0, whiteSpace: "nowrap" }}>
          <Typography variant="body2" color="text.secondary">
            {formatNumber(availability.numLessonUnits)} UE
          </Typography>
          {availability.numDrivingLessons > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                (
              </Typography>
              <CarIcon sx={{ mt: "2px", mr: "2px", fontSize: "16px", color: "text.secondary" }} />
              <Typography
                variant="body2"
                color="text.secondary"
              >{`${availability.numDrivingLessons}×${availability.drivingLessonDuration}min`}</Typography>
              {availability.numPauses > 0 && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {", "}
                  </Typography>
                  <CoffeeOutlinedIcon sx={{ mt: "2px", mx: "1px", fontSize: "14px", color: "text.secondary" }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >{`${availability.numPauses}×${availability.pauseDuration}min`}</Typography>
                </>
              )}
              <Typography variant="body2" color="text.secondary">
                )
              </Typography>
            </>
          )}
        </Row>
      </Row>
      <Column gap="10px">
        <Typography sx={{ fontWeight: 600 }}>{}</Typography>
        {timeRanges.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Verfügbarkeit hinterlegt.
          </Typography>
        )}
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 200px) max-content minmax(0, 200px) max-content",
              alignItems: "center",
              gap: "10px",
              "@container (max-width: 350px)": {
                gap: "6px",
                "& .MuiInputBase-root": { paddingRight: "8px" },
                "& .MuiInputBase-input": { paddingLeft: "8px" },
              },
              "@container (max-width: 300px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                "& > .MuiTypography-root": { display: "none" },
                "& > .MuiIconButton-root": { gridColumn: "1 / -1", justifySelf: "end" },
              },
            }}
          >
            <_TimeInput name={`${day}.${index}.start`} />
            <Typography>-</Typography>
            <_TimeInput name={`${day}.${index}.end`} />
            <IconButton title="Zeitblock entfernen" onClick={() => remove(index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        <Row
          sx={{
            justifyContent: "space-between",
            "@container (max-width: 600px)": {
              flexDirection: "column",
              gap: "8px",
            },
          }}
        >
          <Button startIcon={<AddIcon />} onClick={() => append({})}>
            Zeitblock hinzufügen
          </Button>
          {isDirty && <SaveButton2 />}
        </Row>
      </Column>
    </Card>
  );
}

function _TimeInput({ name }: { name: `${WorkTimeDay}.${number}.${"start" | "end"}` }) {
  return (
    <TimeInput<WorkTimesFormValues, Time>
      source={name}
      label={name.endsWith(".start") ? "Von" : "Bis"}
      size="small"
      toPickerValue={(time) =>
        time ? DateTime.fromObject(Object.fromEntries(Object.entries(time).map(([k, v]) => [k, v || 0]))) : null
      }
      fromPickerValue={(dateTime) => (dateTime ? { hour: dateTime.hour, minute: dateTime.minute } : null)}
      sx={{
        width: "100%",
        "& .MuiInputBase-root": { minWidth: 0 },
        "& .MuiInputLabel-asterisk": { display: "none" },
        "& .MuiFormHelperText-root.Mui-error": { whiteSpace: "nowrap" },
        "@container (max-width: 600px)": {
          "& .MuiFormHelperText-root.Mui-error": { whiteSpace: "normal" },
        },
      }}
      helperText=" "
    />
  );
}

function _RecommendationSettingsCard(props: { instructorId: string }) {
  const { instructorId } = props;

  return (
    <FormCard
      resource="instructorRecommendationSettings"
      id={instructorId}
      title="Einstellungen für automatische Terminvorschläge"
    >
      <Column>
        <NumberInput
          source="maxLessonUnitsPerWeek"
          label="max. Unterrichtseinheiten pro Woche"
          size="small"
          sx={{ width: "100%" }}
        />
        <SelectInput
          source="maxLessonUnitsPerDay"
          label="max. Unterrichtseinheiten pro Tag"
          options={[
            ...([1, 2, 3, 4, 5, 6, 7, 8].map((units) => [units, `${units}`]) as Array<[number, string]>),
            [Number.POSITIVE_INFINITY, "egal"],
          ]}
          size="small"
          helperText=" "
          sx={{ width: "100%" }}
        />
        <SelectInput
          source="minHoursInAdvance"
          label="Vorschläge für Fahrschüler anzeigen"
          options={[12, 24, 48].map((hours) => [hours, `min. ${hours} Stunden im Voraus`])}
          size="small"
          helperText=" "
          sx={{ width: "100%" }}
        />
        <SelectInput
          source="maxDaysBookableInAdvance"
          label="Maximal im Voraus buchbar"
          options={[
            [7, "1 Woche im Voraus"],
            ...[2, 3, 4, 5, 6, 7, 8, 10, 12].map(
              (weeks) => [weeks * 7, `${weeks} Wochen im Voraus`] as [number, string],
            ),
          ]}
          size="small"
          helperText=" "
          sx={{ width: "100%" }}
        />
        <SelectInput
          source="pauseDuration"
          label="Pause zwischen Fahrstunden"
          options={[0, 5, 10, 15, 30].map((minutes) => [minutes, `${minutes} Min.`])}
          size="small"
          helperText=" "
          sx={{ width: "100%" }}
        />
        <SelectInput
          source="drivingLessonDuration"
          label="bevorzugte Dauer einer Übungsfahrt"
          options={[45, 60, 90, 120, 135].map((minutes) => [minutes, `${minutes} Min.`])}
          size="small"
          helperText=" "
          sx={{ width: "100%" }}
        />
        <BooleanInput source="allowOverbooking" label="Mehr als 11 UE pro Tag buchbar" />
      </Column>
    </FormCard>
  );
}

function formatNumber(n: number) {
  return n.toLocaleString("de-DE", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function omitId<T extends { id: string }>(data: T): Omit<T, "id"> {
  const { id: _, ...rest } = data;
  return rest;
}

function validateWorkTimes(formData: WorkTimesFormValues) {
  const errors: Partial<Record<Path<WorkTimesFormValues>, string>> = {};
  for (const day of _workTimeDays) {
    const timeRanges = formData[day] ?? [];
    for (let index = 0; index < timeRanges.length; index++) {
      const { start, end } = timeRanges[index];
      if (!start || isNaN(start.hour) || isNaN(start.minute) || (start.hour === 0 && start.minute === 0)) {
        errors[`${day}.${index}.start`] = "Bitte eingeben.";
      }
      if (!end || isNaN(end.hour) || isNaN(end.minute) || (end.hour === 0 && end.minute === 0)) {
        errors[`${day}.${index}.end`] = "Bitte eingeben.";
      }
      if (start && end && toMinutes(end) <= toMinutes(start)) {
        errors[`${day}.${index}.end`] = `Uhrzeit nach ${
          start.hour.toString().padStart(2, "0") + ":" + start.minute.toString().padStart(2, "0")
        } eingeben.`;
      }
    }
  }
  return errors;
}

function isValidTimeRange(input: { start?: Time; end?: Time }): input is TimeRange {
  const { start, end } = input;
  return !!(start && end);
}

function toMinutes(time: Time): number {
  return time.hour * 60 + time.minute;
}
